import React, { useMemo } from "react";
import {
  Button as MuiButton,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { getStoreForm } from "../../lib/apis/dtos/request/store.request";
import { useStoreList } from "../../lib/apis/hooks/store.hook";
import { useEnterKey } from "../../lib/hooks/useEnterKey";
import SearchInput from "../../components/ui/input/search.component";
import Button from "../../components/ui/button";
import { useModalStore } from "../../store/useModalStore";
import { MODAL_KEY } from "../../lib/constants/modal.constants";

const DEFAULT_PAGE_SIZE = 10;

const n = (v: string | null, d: number) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : d;
};

const STATUS_TYPE = [
  { label: "전체", value: "ALL", countValue: "totalCount" },
  { label: "활성", value: "ACTIVE", countValue: "activeCount" },
  { label: "비활성", value: "INACTIVE", countValue: "inactiveCount" },
];

export default function BranchPage() {
  const { openModal } = useModalStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sp, _] = useSearchParams();

  // URL → form (단일 소스)
  const form = useMemo(
    () => ({
      page: n(sp.get("page"), 1),
      size: n(sp.get("size"), DEFAULT_PAGE_SIZE),
      search: sp.get("search") ?? "",
      activeness: (sp.get("activeness") ?? "ALL") as getStoreForm["activeness"],
    }),
    [sp]
  );

  React.useEffect(() => {
    const params = new URLSearchParams(sp.toString());
    let changed = false;

    if (!params.has("page")) { params.set("page", "0"); changed = true; }
    if (!params.has("size")) { params.set("size", String(DEFAULT_PAGE_SIZE)); changed = true; }
    if (!params.has("search")) { params.set("search", ""); changed = true; }
    if (!params.has("activeness")) { params.set("activeness", "ALL"); changed = true; }

    if (changed) navigate(`${pathname}?${params.toString()}`);
  }, [sp, pathname, navigate]);

  // 쿼리 업데이트 유틸
  const setQuery = (updates: Partial<typeof form>) => {
    const next = { ...form, ...updates };
    const params = new URLSearchParams(sp.toString());
    params.set("page", String(next.page));
    params.set("size", String(next.size));
    params.set("activeness", next.activeness);
    params.set("search", next.search); // 빈 문자열도 그대로 남김 => ?search=
    navigate(`${pathname}?${params.toString()}`);
  };

  // QUERY
  const { data, refetch, isSuccess } = useQuery({
    ...useStoreList(form), // queryKey가 form을 포함해야 함
    enabled: true,
  });

  const { result } = data || {};
  const content = result?.content || [];
  const pageInfo = result?.page;

  // HANDLERS (URL만 갱신)
  const handleChangePage = (
    _e: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => setQuery({ page: newPage });

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setQuery({ page: 0, size: parseInt(e.target.value, 10) });


  useEnterKey(refetch);

  return (
    <div className="p-6">
      <div className="flex items-center p-4">
        <SearchInput
          onChange={(e) =>
            setQuery({ page: 0, search: e.target.value })
          }
          label="지점 검색"
          placeholder="지점 코드, 지점명, 지점 담당자명으로 검색"
          onClick={() => refetch()}
        />
        <div className="h-[32px] w-[0.5px] border-[0.5px] mx-[24px] " />
        <Button
          label="지점 생성"
          className="bg-[#ffffff] text-[14px] w-[120px] h-[36px] text-[#1976D2]  rounded-[3px] border border-[#1976D280]"
          onClick={() => openModal(MODAL_KEY.BranchCreate, {})}
        />
      </div>

      <div className="flex flex-row justify-between items-center px-[16px] mb-[8px]">
        <p className="text-[14px] font-medium">
          전체 {pageInfo?.totalElements || 0}개
        </p>

        <div className="flex flex-row">
          <ButtonGroup
            className="float-right"
            variant="contained"
            aria-label="phone-group"
          >
            {STATUS_TYPE.map(({ label, value }, index) => (
              <MuiButton
                key={index}
                style={{
                  backgroundColor:
                    form.activeness === value
                      ? "rgba(25, 118, 210, 0.5)"
                      : undefined,
                }}
                variant={form.activeness == value ? "contained" : "outlined"}
                onClick={() =>
                  setQuery({ activeness: value as "ALL" | "ACTIVE" | "INACTIVE", page: 0 })
                }
              >
                {label}
              </MuiButton>
            ))}
          </ButtonGroup>
        </div>
      </div>

      <TableContainer>
        <Table className="table-fixed">
          <TableHead>
            <TableRow>
              <TableCell className="flex-1">지점명</TableCell>
              <TableCell className="w-[290px]">영업 지역</TableCell>
              <TableCell className="flex-1">주소</TableCell>
              <TableCell className="flex-1">지점 담당자</TableCell>
              <TableCell className="w-[88px]">활성 여부</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSuccess &&
              content.map(
                (
                  { id, storeName, storeAddress, managers = [], isActive: isActiveRaw, mainZone, subZones, isOnline, isBanned },
                  index
                ) => {
                  const statusMap = {
                    활성: "primary",
                    비활성: "gray",
                  } as const;
                  type statusKey = keyof typeof statusMap;
                  // 정지된 지점은 비활성으로 표기
                  const isActive = isActiveRaw && !isBanned;
                  const statused: statusKey = isActive ? "활성" : "비활성";
                  const color = statusMap[statused];
                  return (
                    <TableRow
                      className="cursor-pointer"
                      key={`branch_${index}`}
                      onClick={() => navigate(`/branch/${id}`)}
                    >
                      <TableCell>
                        <p className="text-ellipsis line-clamp-1">{storeName || "-"}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex-row gap-2 flex-nowrap">
                          {isOnline ? (
                            <p className="text-style-body-03 font-normal">온라인</p>
                          ) : (
                            <>
                              <div className="flex-row gap-1">
                                <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-green-05 text-green-60 whitespace-normal">주</p>
                                <p className="text-style-body-03 font-normal whitespace-normal">{mainZone.region} {mainZone.zoneName}</p>
                              </div>
                              {subZones.length > 0 && (
                                <>
                                  <div className="flex-row gap-1">
                                    <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-gray-05 text-gray-60 whitespace-normal">인근</p>
                                    <p className="text-style-body-03 font-normal">{subZones[0].region} {subZones[0].zoneName} {subZones.length - 1 > 0 ? `외 ${subZones.length - 1}개` : ""}</p>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-ellipsis line-clamp-1">{storeAddress || "-"}</p>
                      </TableCell>
                      <TableCell>
                        {managers.length > 1
                          ? `${managers[0]} 외 ${managers.length - 1}명`
                          : managers[0] ?? "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={color}
                          label={isActive ? "활성" : "비활성"}
                          className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={pageInfo?.totalElements || 0}
        rowsPerPage={form.size}
        page={form.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}
